import { useState, useEffect, useContext, useMemo, forwardRef, useImperativeHandle } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

import toast from "react-hot-toast";
import moment from "moment";

import Prompt from "components/UpStyled/Prompt";
import api from "services/api";
import { nanoid } from "nanoid";

import EventCalendar from "examples/Calendar";
import AddEventModal from "./AddEventModal";
import EventModalViewer from "pages/de/dashboard/components/EventModalViewer";

import rgba from "assets/theme/functions/rgba";
import { convertDbToEvent, convertEventDb } from "utils/helper";

import { AccountContext } from "context/account";

const Calendar = forwardRef(
  ({ handleAfterChange, calRef, initialView = "dayGridMonth", ...rest }, ref) => {
    const [events, setEvents] = useState([]);
    const [startDate, endDate] = useState(moment());
    const [selectedInfo, setSelectedInfo] = useState({});
    const [addEventVisible, setAddEventVisible] = useState(false);
    const [editEventVisible, setEditEventVisible] = useState(false);
    const [viewEventVisible, setViewEventVisible] = useState(false);

    const [currentView, setCurrentView] = useState("Month ▾");
    const [viewMenuVisible, setViewMenuVisible] = useState(null);

    const navigate = useNavigate();
    const account = useContext(AccountContext);

    useImperativeHandle(
      ref,
      () => {
        return {
          retrieveEvents,
        };
      },
      []
    );

    useEffect(() => {
      retrieveEvents();
    }, []);

    const retrieveEvents = async (inactiveIds) => {
      try {
        //get calendar list owned by you
        const temp = await api.calendar.listCalendars({ order: "created_at" });
        const calendars = temp?.data?.calendars || [];

        //get shared calendar list
        const shared = await api.calendar.sharedCalendarList({ order: "created_at" });
        let sharedCalendars = shared?.data?.calendars || [];
        const inactiveCalendars = inactiveIds
          ? inactiveIds
          : account?.metadata?.inactive_calendars || [];

        let cids = calendars.filter((x) => x.active).map((x) => x.id);
        let scids = sharedCalendars
          .filter((x) => !inactiveCalendars.includes(x.id))
          .map((x) => x.id);

        let rawEvents = [];
        let sharedRawEvents = [];

        if (cids.length > 0) {
          const { data } = await api.calendar.listEvents({ calendar_id: cids.join(":") });
          let raw = data?.events || [];
          rawEvents = raw.map((x) => convertDbToEvent(x, account.id));
        }
        if (scids.length > 0) {
          const { data } = await api.calendar.listSharedCalendarEvents({
            calendar_id: scids.join(":"),
          });
          let raw = data?.events || [];
          sharedRawEvents = raw.map((x) => convertDbToEvent(x, account.id));
        }

        setEvents([...rawEvents, ...sharedRawEvents]);
      } catch (err) {
        console.log(err);
        toast.error(`Could not retrieve calendars!`);
      }
    };

    const handleDateClick = (e) => {
      console.log(e);
    };

    const handleDateSelect = (selectInfo) => {
      setAddEventVisible(true);
      setSelectedInfo(selectInfo);
    };

    const handleEventClick = (clickInfo) => {
      setViewEventVisible(true);
      setSelectedInfo(clickInfo);
    };

    const handleEventChange = async ({ event, oldEvent }) => {
      let payload = convertEventDb(event);
      if (!payload?.id) {
        return toast.error("Can't find the event ID!");
      }
      try {
        const { data } = await api.calendar.updateEvent(payload?.id, payload);
        const updated = data?.updated || {};
        const newEvents = [];

        events.forEach((x) => {
          if (x.id == updated.id) {
            const newEvent = convertDbToEvent(updated);
            newEvents.push(newEvent);
          } else {
            newEvents.push(x);
          }
        });
        setEvents(newEvents);
        handleAfterChange();
      } catch (err) {
        return toast.error("Event could not be updated!");
      }
    };

    const handleRefresh = () => {
      retrieveEvents();
    };

    const handleCloseViewMenu = () => setViewMenuVisible(false);

    const ActionMenu = () => (
      <Menu
        id="user-management-menu"
        anchorEl={viewMenuVisible}
        open={Boolean(viewMenuVisible)}
        onClose={handleCloseViewMenu}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={(e) => {
            const calApis = calRef?.current.getApi();
            calApis.changeView("timeGridDay");
            setCurrentView("Day ▾");
            handleCloseViewMenu();
          }}
        >
          <ListItemText>Day</ListItemText>
          <SuiTypography variant="body1" color="text">
            D
          </SuiTypography>{" "}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            const calApis = calRef?.current.getApi();
            calApis.changeView("timeGridWeek");
            setCurrentView("Week ▾");
            handleCloseViewMenu();
          }}
        >
          <ListItemText>Week</ListItemText>
          <SuiTypography variant="body1" color="text">
            W
          </SuiTypography>{" "}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            const calApis = calRef?.current.getApi();
            calApis.changeView("dayGridMonth");
            setCurrentView("Month ▾");
            handleCloseViewMenu();
          }}
        >
          <ListItemText>Month</ListItemText>
          <SuiTypography variant="body1" color="text">
            M
          </SuiTypography>{" "}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            const calApis = calRef?.current.getApi();
            calApis.changeView("multiMonthYear");
            setCurrentView("Year ▾");
            handleCloseViewMenu();
          }}
        >
          <ListItemText>Year</ListItemText>
          <SuiTypography variant="body1" color="text">
            Y
          </SuiTypography>{" "}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            const calApis = calRef?.current.getApi();
            calApis.changeView("listMonth");
            setCurrentView("List ▾");
            handleCloseViewMenu();
          }}
        >
          <ListItemText>List</ListItemText>
          <SuiTypography variant="body1" color="text">
            L
          </SuiTypography>{" "}
        </MenuItem>
      </Menu>
    );

    return (
      <div>
        <EventCalendar
          initialView={initialView}
          headerToolbar={{
            left: "prev,next today,fullView",
            center: "title",
            right: "viewButton",
          }}
          customButtons={{
            fullView: {
              text: "Full view",
              click: function () {
                navigate("/calendar");
              },
            },
            viewButton: {
              text: currentView,
              click: function (e) {
                setViewMenuVisible(e?.target);
              },
            },
          }}
          initialDate={startDate.format("yyyy-MM-DD")}
          events={events}
          selectable
          editable
          dayMaxEvents={2}
          height={650}
          dateClick={handleDateClick}
          select={handleDateSelect}
          eventClick={handleEventClick}
          eventChange={handleEventChange} // called after events are initialized/added/changed/removed
          /* you can update a remote database when these fire:
      eventAdd={function(){}}
      eventChange={function(){}}
      eventRemove={function(){}}
      */
          moreLinkClick={"popover"}
          scrollTime="08:00:00"
          ref={calRef}
          {...rest}
        />
        <ActionMenu />
        {addEventVisible && selectedInfo ? (
          <AddEventModal
            visible={addEventVisible}
            selectInfo={selectedInfo}
            handleClose={() => setAddEventVisible(false)}
            handleRefresh={handleRefresh}
            handleAfterOk={handleAfterChange}
          />
        ) : null}
        {editEventVisible && selectedInfo ? (
          <AddEventModal
            visible={editEventVisible}
            selectInfo={selectedInfo}
            isEdit={true}
            handleClose={() => setEditEventVisible(false)}
            handleRefresh={handleRefresh}
            handleAfterOk={handleAfterChange}
          />
        ) : null}
        {viewEventVisible && selectedInfo ? (
          <EventModalViewer
            visible={viewEventVisible}
            selectInfo={selectedInfo}
            handleOpenEdit={() => {
              setViewEventVisible(false);
              setEditEventVisible(true);
            }}
            handleClose={() => setViewEventVisible(false)}
            handleRefresh={handleRefresh}
            handleAfterOk={handleAfterChange}
          />
        ) : null}
      </div>
    );
  }
);

export default Calendar;
